import {Injectable, computed, signal} from '@angular/core';
import {ToastType} from "../enums/ToastType";
import type {Toast} from "../interfaces/Toast";

const TITLES: Partial<Record<ToastType, string>> = {
  [ToastType.Success]: 'Готово',
  [ToastType.Danger]: 'Ошибка',
  [ToastType.Warning]: 'Внимание',
  [ToastType.Info]: 'Информация',
};

const MAX_TOASTS = 50;

/**
 * Тосты на сигналах: OnPush-компонент перерисовывается, даже если тост добавлен
 * из чужого компонента или HTTP-колбэка. У каждого тоста свой таймер — закрытие
 * одного не останавливает исчезновение остальных.
 */
@Injectable({
  providedIn: 'root'
})
export class ToastsService {
  timeOut = 4000;

  private nextId = 1;
  private timers = new Map<number, ReturnType<typeof setTimeout>>();
  private readonly _toasts = signal<Toast[]>([]);

  readonly toasts = this._toasts.asReadonly();
  /** Для вывода: новые сверху (копия — исходный массив не мутируется). */
  readonly newestFirst = computed(() => [...this._toasts()].reverse());

  pushToast(message: { message: string, type: ToastType }) {
    const toast: Toast = {
      ...message,
      id: this.nextId++,
      title: TITLES[message.type] ?? 'Сообщение',
    };

    const list = [...this._toasts(), toast];
    // лишние старые — убираем вместе с их таймерами
    for (const old of list.splice(0, Math.max(0, list.length - MAX_TOASTS))) {
      this.clearTimer(old.id!);
    }
    this._toasts.set(list);

    this.timers.set(toast.id!, setTimeout(() => this.removeToast(toast.id!), this.timeOut));
  }

  removeToast(id: number) {
    this.clearTimer(id);
    this._toasts.update(list => list.filter(t => t.id !== id));
  }

  removeAllToasts() {
    this.timers.forEach(timer => clearTimeout(timer));
    this.timers.clear();
    this._toasts.set([]);
  }

  private clearTimer(id: number) {
    clearTimeout(this.timers.get(id));
    this.timers.delete(id);
  }
}
